// New component: src/components/DomainConfigModal.tsx
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Clipboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface DomainConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  feedback: {
    title: string;
    message: string;
    dnsInstructions?: string;
    txtValue?: string;
  } | null;
}

export function DomainConfigModal({
  open,
  onOpenChange,
  loading,
  feedback,
}: DomainConfigModalProps) {
  const { toast } = useToast();

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    toast({ title: 'Copied', description: 'TXT value copied to clipboard' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{feedback?.title || 'Configuring Domain'}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className='flex justify-center items-center p-4'>
            <Loader2 className='h-6 w-6 animate-spin' />
            <span className='ml-2'>Processing...</span>
          </div>
        ) : (
          <>
            <DialogDescription>{feedback?.message}</DialogDescription>
            {feedback?.dnsInstructions && (
              <p className='mt-2 text-sm'>{feedback.dnsInstructions}</p>
            )}
            {feedback?.txtValue && (
              <div className='mt-4 flex items-center gap-2'>
                <Input value={feedback.txtValue} readOnly className='flex-1' />
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleCopy(feedback.txtValue)}
                >
                  <Clipboard className='h-4 w-4 mr-2' />
                  Copy
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
